import {ProductOfferShort} from 'models';
import {CHECKED_LIST_CONDITIONS} from 'modules/cart/constants';
import {cartModuleName, actionSetCart} from 'modules/cart/slice';
import {ICartState, TCartItem, TCheckedListCondition, TRemoveModalView} from 'modules/cart/types';
import {selectIsLoading} from 'modules/loader/selectors';
import {TAppRootState} from 'core/redux/types';
import {createDraftSafeSelector} from '@reduxjs/toolkit';

const select = (state: TAppRootState): ICartState => state[cartModuleName];

export const selectCartProductsList = createDraftSafeSelector([select], (state) => state.productsList);

export const selectCartTotal = (state: TAppRootState): ICartState['total'] => select(state).total;

export const selectCartProductById = (id?: ProductOfferShort['id'] | null) =>
    createDraftSafeSelector([selectCartProductsList], (cartProductList) => {
        return cartProductList.find(({id: productId}) => productId === id);
    });

export const selectCartProductCount = (id: ProductOfferShort['id']) =>
    createDraftSafeSelector([(state) => selectCartProductById(id)(state)], (product) => {
        if (!product) {
            return 0;
        }

        return product.inBasket;
    });

export const selectCartIsLegalEntity = (state: TAppRootState): boolean => select(state).isLegalEntity;

export const selectCartAfterRemoveToast = (state: TAppRootState): string => select(state).afterRemoveToast;

export const selectCartRemoveModalView = (state: TAppRootState): TRemoveModalView => select(state).removeModalView;

export const selectCartCheckedList = createDraftSafeSelector([select], (state) => state.checkedList);

export const selectCartIsOpenAvailabilities = (state: TAppRootState): boolean => select(state).isOpenAvailabilities;

export const selectCartIsChecked = (id: ProductOfferShort['id']) =>
    createDraftSafeSelector([selectCartCheckedList], (cartCheckedListId) => cartCheckedListId.includes(id));

export const selectCartCheckedListCondition = createDraftSafeSelector(
    [selectCartCheckedList, selectCartProductsList],
    (cartCheckedList, cartProductList): TCheckedListCondition => {
        if (!cartCheckedList.length) {
            return CHECKED_LIST_CONDITIONS.NONE;
        }

        if (1 === cartCheckedList.length) {
            return CHECKED_LIST_CONDITIONS.ONE;
        }

        if (cartCheckedList.length === cartProductList.length) {
            return CHECKED_LIST_CONDITIONS.ALL;
        }

        return CHECKED_LIST_CONDITIONS.SOME;
    },
);

export const selectCartRemoveList = createDraftSafeSelector(
    [selectCartProductsList, selectCartCheckedList],
    (cartProductList, checkedIdList): TCartItem[] => {
        return cartProductList
            .filter(({id}) => checkedIdList.includes(id))
            .map(({id}) => ({amount: 0, productOfferId: id}));
    },
);

export const selectCartRemoveId = (state: TAppRootState): ProductOfferShort['id'] | null => select(state).removeId;
export const selectCartSelectedProductId = (state: TAppRootState): ProductOfferShort['id'] | null =>
    select(state).selectedProductId;

export const selectIsCartLoads = (state: TAppRootState) => selectIsLoading(actionSetCart.type, true)(state);

export const selectIsCartCountLoads = (id: number) => (state: TAppRootState) =>
    selectIsLoading(`${actionSetCart.type}__id:${id}`)(state);

export const selectCartNewAdded = (state: TAppRootState) => select(state).newAdded;
